import { Injectable, OnDestroy } from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { Store } from "@ngrx/store";
import {
	FraudPreventionConfigService,
	FraudPreventionService,
	GroupIBFraudPreventionService,
	TwoFactorAuthAction,
	UserDeviceService,
} from "@obg/ngx.common";
import {
	AppContextConfigService,
	CustomerManagementSelector,
	LoginRequest,
	OperatingSystemService,
	PlatformName,
	SessionAction,
	SessionSelector,
} from "@obg/ngx.core";
import { FeatureFlag, FeatureFlagService } from "@obg/ngx.global";
import { CookieService } from "@obg/ngx.global.evo";
import { ignoreNil } from "@obg/rxjs";
import { trim } from "lodash-es";
import { combineLatest, EMPTY, merge, Observable, Subscription } from "rxjs";
import { map, take, tap } from "rxjs/operators";

import { EmailLoginSelector } from "../sdk";

import { LoginFormGroup, MfeLoginFlag } from "./login.model";
import { parseJwt } from "./utils/jwt/jwt.util";

export const MFE_LOGIN_FLAG_KEY = "mfe_login";
/**
 * This service contains fields and logic for login functionality
 * Should be registered on component level
 */
@Injectable({ providedIn: "root" })
export class LoginFormService implements OnDestroy {
	readonly loginForm: LoginFormGroup;
	readonly isIOS: boolean;
	readonly isAndroid: boolean;

	private data$$: Subscription | undefined;

	private currentDeviceId: string | undefined;
	private _mfeLoginFlag: MfeLoginFlag | undefined;

	constructor(
		private store: Store,
		private sessionSelector: SessionSelector,
		private sessionAction: SessionAction,
		private formBuilder: UntypedFormBuilder,
		private fraudPreventionConfig: FraudPreventionConfigService,
		private fraudPreventionService: FraudPreventionService,
		private groupIBFraudPreventionService: GroupIBFraudPreventionService,
		private cookieService: CookieService,
		private appContextConfigService: AppContextConfigService,
		private operatingSystemService: OperatingSystemService,
		private userDeviceService: UserDeviceService,
		private featureFlagService: FeatureFlagService,
		private emailLoginSelector: EmailLoginSelector
	) {
		this.loginForm = this.createLoginForm();
		this.isIOS = this.operatingSystemService.getOperatingSystem() === PlatformName.IOS;
		this.isAndroid = this.operatingSystemService.getOperatingSystem() === PlatformName.Android;
	}

	login(disallowRedirect = false, disallowLogin = false) {
		const loginRequest: LoginRequest = {
			...this.loginForm.value,
			deviceId: this.currentDeviceId,
			options: {
				disallowRedirect,
				disallowLogin,
			},
		};

		this.store.dispatch(this.sessionAction.login(loginRequest));
	}

	loginFabric(loginValues: any) {
		const loginRequest = {
			...loginValues,
			deviceId: this.currentDeviceId,
		};

		this.store.dispatch(this.sessionAction.login(loginRequest));
	}

	initialize() {
		if (this.data$$) {
			throw new Error("Already initialized");
		}

		this.store.dispatch(TwoFactorAuthAction.loadCurrentDevice());
		this.data$$ = merge(this.fraudPrevention$(), this.restoreUserName$()).subscribe();
	}

	ngOnDestroy() {
		if (this.fraudPreventionConfig.getEnabledInLogin()) {
			this.fraudPreventionService.unloadScript();
		}

		this.data$$?.unsubscribe();
	}

	hasEmptyFields(): boolean {
		return !trim(this.loginForm.controls.username.value) || !trim(this.loginForm.controls.password.value);
	}

	/**
	 * Creates the Login Form based on the LoginFormGroup model
	 */
	private createLoginForm(): LoginFormGroup {
		return this.formBuilder.group({
			username: this.formBuilder.control("", [Validators.required]),
			password: this.formBuilder.control("", [Validators.required]),
			shouldRememberUser: this.formBuilder.control(true),
			iovationBlackBox: this.formBuilder.control(""),
			fraudToken: this.formBuilder.control(this.groupIBFraudPreventionService.getSessionId()),
		}) as LoginFormGroup;
	}

	private fraudPrevention$(): Observable<any> {
		if (!this.fraudPreventionConfig.getEnabledInLogin()) {
			return EMPTY;
		}

		this.fraudPreventionService.loadScript();

		return this.fraudPreventionService.getCustomerFraudToken$().pipe(
			ignoreNil(),
			tap(() => {
				const token = this.groupIBFraudPreventionService.getSessionId();
				this.loginForm.controls.iovationBlackBox.setValue(token);
				this.loginForm.controls.fraudToken.setValue(token);
			})
		);
	}

	private vipUsernamePrefill(): string | undefined {
		const vipCookieDetails = this.appContextConfigService.getVipCookieDetails();
		if (!vipCookieDetails.cookieName || !vipCookieDetails.emailField) {
			return undefined;
		}

		const vipCookie = this.cookieService.get(vipCookieDetails.cookieName);
		if (!vipCookie) {
			return undefined;
		}
		const vipCookieParsed = parseJwt(vipCookie);
		return vipCookieParsed && vipCookieParsed[vipCookieDetails.emailField];
	}

	private restoreUserName$(): Observable<void> {
		return combineLatest([
			this.store.select(CustomerManagementSelector.getExpiredSessionUsername),
			this.store.select(this.sessionSelector.getLastActiveUsername()),
			this.store.select(this.emailLoginSelector.emailLogin()),
			this.userDeviceService.getUserDevice$(),
		]).pipe(
			take(1),
			tap(([username, rememberedUsername, email, currentDevice]) => {
				const usernameValue = username || this.vipUsernamePrefill() || rememberedUsername || email;
				// DO not update the form if received data are undefined
				if (usernameValue) {
					this.loginForm.patchValue({
						username: usernameValue,
						password: "",
					});
				}
				this.currentDeviceId = currentDevice?.visitorId;
			}),
			map(() => undefined)
		);
	}

	private getMfeLoginFlag(): FeatureFlag<MfeLoginFlag | undefined> | undefined {
		return this.featureFlagService.get<MfeLoginFlag | undefined>(MFE_LOGIN_FLAG_KEY);
	}

	mfeLoginFlag(): boolean | undefined {
		this._mfeLoginFlag = this.getMfeLoginFlag();
		return this._mfeLoginFlag?.enabled;
	}
}
